import ErrorBoundaryFallback from '@components/ErrorBoundary';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import { SupportedEntitlements } from '@core/models/entitlements';
import { SubscriptionsSetupMFEProps } from '@cw-elements/host-kit/subscriptions';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { Suspense, lazy, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const SubscriptionSetupComponent = lazy(() =>
  loadRemoteModule<SubscriptionsSetupMFEProps>('subscriptions/setup')
);

const ENTITLEMENTS_WITH_BYPASS_ENABLED = [
  {
    flag: 'FlagShowNdviReportDrawer',
    entitlement: SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT,
  },
] as const;

const ErrorFallback = () => <ErrorBoundaryFallback />;

export const SubscriptionSetup = () => {
  const { organization } = useOrganization();
  const flags = useImageryFlags(organization);
  const [params, setParams] = useSearchParams();
  const paramsScreen = params.get('subscriptionsSettings');
  const [language] = useSystemLanguage();
  const isOpen = Boolean(params.get('subscriptionsSettings'));

  const onCloseNotificationSettings = () => {
    params.delete('subscriptionsSettings');
    setParams(params);
  };

  const bypassEntitlements = useMemo(() => {
    return ENTITLEMENTS_WITH_BYPASS_ENABLED.filter((e) => flags[e.flag]).map((e) => e.entitlement);
  }, [flags]);

  if (organization?.workspace_id) {
    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <Suspense fallback={<div></div>}>
          <SubscriptionSetupComponent
            bypassEntitlements={bypassEntitlements}
            screen={paramsScreen}
            setScreen={(screen) => {
              params.set('subscriptionsSettings', screen);
              setParams(params);
            }}
            onClose={onCloseNotificationSettings}
            open={isOpen}
            userLanguage={language}
            workspaceId={organization.workspace_id}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }
  return null;
};
